import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import { EmptyStateRadar } from '@rossum/ui';
import { IconFolderOff, IconSearch } from '@rossum/ui/icons/tabler';
import { Dialog, Stack } from '@rossum/ui/material';
import { AddQueueDialog } from '../../queue-sidebar/add-queue/AddQueueDialog';
import { QueueDelete } from '../../queue-sidebar/sidebar-queues/components/QueueDelete';
import { QueueDuplicate } from '../../queue-sidebar/sidebar-queues/components/QueueDuplicate';
import { QueueMove } from '../../queue-sidebar/sidebar-queues/components/QueueMove';
import { WorkspaceDelete } from '../../queue-sidebar/sidebar-queues/components/WorkspaceDelete';
import { AddWorkspaceModal } from '../../queue-sidebar/sidebar/components/AddWorkspaceModal';
import { WorkspaceWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { useQueueListDialogs } from '../hooks/useQueueListDialogs';
import { useRulesCountMap } from '../hooks/useRulesCountMap';
import { EngineChipData } from '../types';
import { QueueTile } from './queue-tile/QueueTile';
import { WorkspaceSection } from './WorkspaceSection';

type QueuesListProps = {
  allWorkspacesWithQueues: WorkspaceWithQueues[];
  workspaces: Workspace[] | undefined;
  isQueuesFetching: boolean;
  getEngineChipData: (queue: Queue) => EngineChipData | undefined;
  isFiltered: boolean;
  onClearFilters: () => void;
};

export const QueuesList = ({
  allWorkspacesWithQueues,
  workspaces,
  isQueuesFetching,
  getEngineChipData,
  isFiltered,
  onClearFilters,
}: QueuesListProps) => {
  const intl = useIntl();

  // Extract queue IDs from visible workspaces to fetch rules only for displayed queues
  const visibleQueueIds = useMemo(
    () =>
      allWorkspacesWithQueues?.flatMap(workspace =>
        workspace.queues.map(queue => queue.id)
      ) ?? [],
    [allWorkspacesWithQueues]
  );

  const { rulesCountMap } = useRulesCountMap(visibleQueueIds);

  const [dialogState, dispatch] = useQueueListDialogs();

  const selectedWorkspace = useMemo(() => {
    if (dialogState.type === 'workspaceDelete') {
      return workspaces?.find(ws => ws.id === dialogState.workspaceId);
    }
    return undefined;
  }, [workspaces, dialogState]);

  const selectedWorkspaceWithQueues = useMemo(() => {
    if (dialogState.type === 'workspaceAddQueue') {
      return allWorkspacesWithQueues?.find(
        ws => ws.id === dialogState.workspaceId
      );
    }
    return undefined;
  }, [allWorkspacesWithQueues, dialogState]);

  if (!allWorkspacesWithQueues || allWorkspacesWithQueues.length === 0) {
    // A filtered-to-empty list is a "no matches" state, not the first-run
    // onboarding prompt — offer to clear filters instead of creating a workspace.
    if (isFiltered) {
      return (
        <Stack sx={{ height: 500 }}>
          <EmptyStateRadar
            title={intl.formatMessage({
              id: 'features.queueManager.emptyState.noResults.title',
            })}
            description={intl.formatMessage({
              id: 'features.queueManager.emptyState.noResults.description',
            })}
            buttonProps={{
              onClick: onClearFilters,
              text: intl.formatMessage({
                id: 'features.queueManager.emptyState.noResults.clearFilters',
              }),
            }}
            iconProps={{
              Icon: IconSearch,
              iconSize: 32,
            }}
          />
        </Stack>
      );
    }

    return (
      <Stack sx={{ height: 500 }}>
        <EmptyStateRadar
          title={intl.formatMessage({
            id: 'features.queueManager.emptyState.title',
          })}
          description={intl.formatMessage({
            id: 'features.queueManager.emptyState.description',
          })}
          buttonProps={{
            onClick: () => dispatch({ type: 'openAddWorkspace' }),
            text: intl.formatMessage({
              id: 'features.queueManager.actions.addWorkspace',
            }),
          }}
          iconProps={{
            Icon: IconFolderOff,
            iconSize: 32,
          }}
        />
        <AddWorkspaceModal
          open={dialogState.type === 'addWorkspace'}
          onClose={() => dispatch({ type: 'close' })}
        />
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={4}>
        {allWorkspacesWithQueues.map(workspace => (
          <WorkspaceSection
            key={workspace.id}
            id={workspace.id}
            name={workspace.name}
            queues={workspace.queues}
            queuesAreLoading={isQueuesFetching}
            onAction={action =>
              dispatch({
                type: 'openWorkspaceDialog',
                action,
                workspaceId: workspace.id,
              })
            }
            renderQueues={queues =>
              queues.map(queue => (
                <QueueTile
                  key={`queue-${queue.id}-${queue.name}`}
                  queue={queue}
                  onAction={action =>
                    dispatch({ type: 'openQueueDialog', action, queue })
                  }
                  engineChipData={getEngineChipData(queue)}
                  rulesCount={rulesCountMap.get(queue.id) ?? 0}
                />
              ))
            }
          />
        ))}
      </Stack>

      <Dialog
        open={dialogState.type === 'queueMove'}
        onClose={() => dispatch({ type: 'close' })}
        slotProps={{
          paper: {
            sx: { width: 480, position: 'fixed' },
            elevation: 2,
          },
        }}
      >
        {dialogState.type === 'queueMove' && (
          <QueueMove
            queue={dialogState.queue}
            workspaces={allWorkspacesWithQueues ?? []}
            onMove={() => dispatch({ type: 'close' })}
            onCancel={() => dispatch({ type: 'close' })}
          />
        )}
      </Dialog>

      <Dialog
        open={dialogState.type === 'queueDelete'}
        onClose={() => dispatch({ type: 'close' })}
        slotProps={{
          paper: {
            sx: { width: 480, position: 'fixed' },
            elevation: 2,
          },
        }}
      >
        {dialogState.type === 'queueDelete' && (
          <QueueDelete
            queueId={dialogState.queue.id}
            queueName={dialogState.queue.name}
            onDelete={() => dispatch({ type: 'close' })}
            onCancel={() => dispatch({ type: 'close' })}
          />
        )}
      </Dialog>

      <Dialog
        open={dialogState.type === 'queueDuplicate'}
        onClose={() => dispatch({ type: 'close' })}
        slotProps={{
          paper: {
            sx: { width: 480, position: 'fixed' },
            elevation: 2,
          },
        }}
      >
        {dialogState.type === 'queueDuplicate' && (
          <QueueDuplicate
            queue={dialogState.queue}
            onDuplicate={() => dispatch({ type: 'close' })}
            onCancel={() => dispatch({ type: 'close' })}
          />
        )}
      </Dialog>

      <Dialog
        open={
          dialogState.type === 'workspaceDelete' &&
          selectedWorkspace !== undefined
        }
        onClose={() => dispatch({ type: 'close' })}
        slotProps={{
          paper: {
            sx: { width: 480, minHeight: 213, position: 'fixed' },
            elevation: 2,
          },
        }}
      >
        {dialogState.type === 'workspaceDelete' && selectedWorkspace && (
          <WorkspaceDelete
            workspace={selectedWorkspace}
            onDelete={() => dispatch({ type: 'close' })}
            onCancel={() => dispatch({ type: 'close' })}
          />
        )}
      </Dialog>

      <AddQueueDialog
        open={
          dialogState.type === 'workspaceAddQueue' &&
          selectedWorkspaceWithQueues !== undefined
        }
        onClose={() => dispatch({ type: 'close' })}
        onSuccess={() => dispatch({ type: 'close' })}
        workspaceUrl={selectedWorkspaceWithQueues?.url ?? ''}
      />
    </>
  );
};
